export const useNavCategoryApi = () => {
  const navStore = useNavCategoryStore();

  /**
   * Function to fetch all navigation categories with caching and error handling
   * @param locale - The locale in which categories should be fetched
   * @returns Array of categories
   */
  const fetchAllCategories = async (locale: string): Promise<any[]> => {
    try {
      // Early return if categories are already cached in the store
      if (navStore.categories?.length > 0) {
        return navStore.categories;
      }

      navStore.setLoading(true);

      // Fetch categories using `getNavigationCategories`
      const response = await getNavigationCategories(locale, true);

      // If the response is valid, cache and return the categories
      if (response?.success && response.categories) {
        navStore.setCategories(response.categories);
        return response.categories;
      }

      console.warn('[FetchAllCategories] Response was invalid, returning empty array');
      return [];
    } catch (error: any) {
      console.error('[FetchAllCategories] Failed to fetch categories:', error.message || error);
      return [];
    } finally {
      navStore.setLoading(false);
    }
  };

  /**
   * Get navigation category by category name
   * @param categoryId - The name or identifier of the navigation category
   * @param useStore - If true, stores the data in Pinia; otherwise, returns the data directly
   */
  const getNavCategoryById = async (
    categoryId: string,
    locale?: string,
    idType: string = 'DJUST_ID',
    useStore = true
  ): Promise<CategoryResponse | null> => {
    try {
      // Use `useFetch` to fetch category data
      const { data, error } = await useFetch<CategoryResponse>(`/api/categories/${categoryId}`, {
        query: { locale, idType },
      });

      // Handle fetch errors
      if (error.value) {
        console.error('Error fetching category:', error.value?.data?.message || error.value);
        return null;
      }

      // Validate and process the fetched data
      if (data.value?.success) {
        const categoryData = data.value.category;

        if (useStore) {
          // Safely store category data in the store
          navStore.setCategoryData(categoryId, categoryData);
        }

        return data.value; // Return the fetched data
      } else {
        console.error('No valid category data found');
        return null;
      }
    } catch (err) {
      // Handle unexpected errors
      console.error('An unexpected error occurred while fetching the category:', err);
      return null;
    }
  };

  /**
   * Get navigation categories list
   * @param locale - The locale in which categories should be fetched
   * @param useStore - If true, stores the data in Pinia; otherwise, returns the data directly
   */
  const getNavigationCategories = async (
    locale: string,
    useStore = true
  ): Promise<CategoriesResponse> => {
    try {
      // Use `useFetch` to fetch navigation categories
      const { data, error } = await useFetch<CategoriesResponse>(`/api/categories`, {
        query: { locale },
      });

      // Handle errors from `useFetch`
      if (error.value) {
        console.error(
          'Failed to fetch navigation categories:',
          error.value?.data?.message || error.value
        );
        return { success: false, categories: [] }; // Return a valid `CategoriesResponse`
      }

      // Store the data in Pinia if `useStore` is true and data is valid
      if (useStore && data.value?.success) {
        navStore.setCategories(data.value.categories);
      }

      // Return the fetched data
      return data.value || { success: false, categories: [] }; // Ensure a valid return type
    } catch (err) {
      console.error('Unexpected error while fetching navigation categories:', err);
      // Return a valid `CategoriesResponse` on unexpected errors
      return { success: false, categories: [] };
    }
  };

  /**
   * Get navigation category breadcrumbs
   * @param category - The name or identifier of the navigation category
   * @param locale - The locale in which to fetch the breadcrumbs
   * @param useStore - If true, stores the data in Pinia; otherwise, returns the data directly
   */
  const getNavCategoryBreadcrumbs = async (
    categoryId: string,
    locale?: string,
    idType: string = 'DJUST_ID',
    useStore = true
  ): Promise<CategoryBreadcrumbsResponse> => {
    try {
      // Fetch breadcrumbs using `useFetch`
      const { data, error } = await useFetch<CategoryBreadcrumbsResponse>(
        `/api/categories/${categoryId}/breadcrumbs`,
        {
          query: { locale, idType },
        }
      );

      // Handle fetch errors
      if (error.value) {
        console.error(
          'Error fetching category breadcrumbs:',
          error.value?.data?.message || error.value
        );
        // Return a default response to satisfy type requirements
        return { success: false, breadcrumbs: null };
      }

      // Validate and process the fetched data
      if (data.value?.success) {
        const breadcrumbs = data.value.breadcrumbs;

        if (useStore) {
          // Safely store breadcrumbs data in the store
          navStore.setBreadcrumbs(categoryId, breadcrumbs);
        }

        return data.value;
      } else {
        console.error('No valid category breadcrumbs data found');
        // Return a default response to handle missing data gracefully
        return { success: false, breadcrumbs: null };
      }
    } catch (err) {
      // Handle unexpected errors
      console.error('An unexpected error occurred while fetching the category breadcrumbs:', err);
      // Return a default response for unexpected errors
      return { success: false, breadcrumbs: null };
    }
  };

  return {
    fetchAllCategories,
    getNavCategoryById,
    getNavigationCategories,
    getNavCategoryBreadcrumbs,
  };
};
